import {
  CUSTOM_ELEMENTS_SCHEMA,
  NgModule
} from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { metaReducers, reducers } from '@literax/store';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AuthEffects } from '@literax/modules/auth/store/auth.effects';
import { AuthInterceptor } from './interceptors/auth-interceptor';
import { AuthService } from './modules/auth/services/auth.service';
import { B2CAuthModule } from './b2c-auth/b2c-auth.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { CompanyEffects } from '@literax/modules/configurations/store/company/company.effects';
import { DocumentsService } from './services/platform/documents.service';
import { EffectsModule } from '@ngrx/effects';
import { ErrorResetPasswordComponent } from './components/auth/error-password/error-password.component';
import { FreeTermsEffects } from './store/free-terms/free-terms.effects';
import { HttpLoaderFactory } from '@literax/services/translate/http-loader.factory';
import { LoadingInterceptor } from './interceptors/loading.interceptor';
import { LoginClientusersComponent } from './components/auth/login-clientusers/login-clientusers.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { OnboardingComponent } from './components/auth/onboarding/onboarding.component';
import { ProfilesEffect } from './components/configurations/profiles/states/profiles.effects';
import { RegisterClientusersComponent } from './components/auth/register-clientusers/register-clientusers.component';
import { RegistrationEffects } from './store/registration/registration.effects';
import { ReportEffects } from '@literax/store/report/report.effects';
import { ReportPromissoryNoteEffects } from './store/report/report-promissory-note.effects';
import { SharedModule } from './modules/shared/shared.module';
import { SignRequestEffects } from './modules/documents/store/sign-request/sign-request.effects';
import { SignaturesModule } from './components/signatures/signatures.module';
import { StatusEffects } from './store/status/status.effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreModule } from '@ngrx/store';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { TermsEffects } from './store/terms/terms.effects';
import { ToastrModule } from 'ngx-toastr';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { UsersEffect } from './components/configurations/users/states/users.effects';
import { ValidatorModule } from './modules/validator/validator.module';
import { environment } from '@environments/environment';
import {
  HighlightModule,
  HighlightOptions,
  HIGHLIGHT_OPTIONS,
} from 'ngx-highlightjs';
import { BackButtonEffects } from './store/back-button/back-button.effects';
import { StepButtonsComponent } from './components/auth/onboarding/step-buttons/step-buttons.component';
import { MembershipCardComponent } from './components/auth/onboarding/membership-card/membership-card.component';
import { EmailValidationComponent } from './components/auth/email-validation/email-validation.component';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideNzConfig } from 'ng-zorro-antd/core/config';

export function httpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [
    AppComponent,
    ErrorResetPasswordComponent,
    OnboardingComponent,
    LoginClientusersComponent,
    RegisterClientusersComponent,
    StepButtonsComponent,
    MembershipCardComponent,
    EmailValidationComponent
  ],
  imports: [
    SharedModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SignaturesModule,
    ValidatorModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot([
      AuthEffects,
      RegistrationEffects,
      StatusEffects,
      SignRequestEffects,
      UsersEffect,
      ProfilesEffect,
      ReportEffects,
      CompanyEffects,
      ReportPromissoryNoteEffects,
      TermsEffects,
      FreeTermsEffects,
      BackButtonEffects
    ]),
    StoreRouterConnectingModule.forRoot(),
    StoreDevtoolsModule.instrument({
      name: 'Interfirma UI',
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true,
    }),
    AppRoutingModule,
    ToastrModule.forRoot({ maxOpened: 5 }),
    NgbModule,
    B2CAuthModule,
    HighlightModule,
  ],
  providers: [
    AuthService,
    DocumentsService,
    // ng2-charts 6 dropped NgChartsModule, which registered Chart.js's defaults (hop 17).
    provideCharts(withDefaultRegisterables()),
    // Replaces HttpClientModule, deprecated in Angular 18; interceptors still come from HTTP_INTERCEPTORS (hop 18).
    provideHttpClient(withInterceptorsFromDi()),
    // NG-ZORRO 18 removes the tab being left only after a 300 ms cross-fade, during which both tabs' components exist
    // and the old one lies over the new one. Without the pane animation the switch is immediate, as the content's
    // was in NG-ZORRO 16; the ink bar keeps its animation (hop 18).
    provideNzConfig({ tabs: { nzAnimated: { inkBar: true, tabPane: false } } }),
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: <HighlightOptions>{
        lineNumbers: true,
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        lineNumbersLoader: () => import('highlightjs-line-numbers.js'),
        themePath: 'node_modules/highlight.js/styles/github.css',
        languages: {
          xml: () => import('highlight.js/lib/languages/xml'),
        },
      },
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor(private translate: TranslateService) {
    this.translate.addLangs(['es', 'en']);
    this.translate.setDefaultLang('es');
    this.translate.use('es');
  }
}
